import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiEdit2, FiImage, FiPlus, FiRepeat } from 'react-icons/fi';

import Button from 'components/Button';
import Flag from 'components/Flag';
import Loader from 'components/ContentLoader/GenericLoader';
import NotFound from 'components/NotFound';
import Pagination from 'components/Pagination';

import NoSearch2 from 'assets/nosearch2.svg';
import AppError from 'utils/AppError';
import api from 'services/api';

import { Content, RecriaCardModal, Scroll, Table } from '../styles';
import { Programa } from 'pages/@types';
import { addHours, format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useModal } from 'hooks/modal';

export interface IOportunidade {
  id: number;
  dataexibicao: string;
  programa: Programa;
  titulo?: string;
  resumo?: string;
  palavraschaves?: string;
  card_url: string;
  created_at: string;
  statuscard: 'pendente' | 'processado' | 'erro';
}

interface OportunidadeProps {
  setPalavraChaveSearch: React.Dispatch<React.SetStateAction<string>>;
  setActiveTabIndex: React.Dispatch<React.SetStateAction<number>>;
}

export default function Oportunidade({
  setPalavraChaveSearch,
  setActiveTabIndex,
}: OportunidadeProps) {
  const navigate = useNavigate();
  const { showModal, hideModal } = useModal();
  const [oportunidades, setOportunidades] = useState<IOportunidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const submitRecriarCard = useCallback(
    async ora => {
      try {
        const response = await api.patch(`/ora/${ora.id}/statuscard`);
        if (response && response.data) {
          hideModal();
          toast.success('Solicitado novo card');
          setOportunidades(prevState =>
            prevState.map(p =>
              p.id === ora.id
                ? { ...p, statuscard: 'pendente', card_url: '' }
                : p,
            ),
          );
        } else toast.error('Falha de comunicação com o servidor');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
    },
    [hideModal],
  );

  const handleRecriarCard = useCallback(
    ora => {
      showModal({
        component: (
          <RecriaCardModal>
            <p>Criar novo card para: {ora.titulo}?</p>
            <Button
              color="primary"
              onClick={() => {
                submitRecriarCard(ora);
              }}
            >
              CONFIRMAR
            </Button>
          </RecriaCardModal>
        ),
      });
    },
    [showModal, submitRecriarCard],
  );

  const handlePalavraChaveSearch = (keyword: string) => {
    setPalavraChaveSearch(keyword);
    setActiveTabIndex(2);
  };

  useEffect(() => {
    const controller = new AbortController();
    async function loadOportunidades() {
      setLoading(true);
      try {
        const response = await api.get('/ora', {
          signal: controller.signal,
          params: {
            page,
          },
        });
        if (response && response.data) {
          setPages(response.data.pages);
          setOportunidades(response.data.oportunidades);
        } else toast.error('Falha de comunicação com o servidor');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    loadOportunidades();
    return () => {
      controller.abort();
    };
  }, [page]);

  return (
    <Content>
      <header>
        <h4>LISTA DE OPORTUNIDADES</h4>
        <Pagination
          total={pages}
          activePage={page}
          onClick={page => setPage(page)}
        />
        <Button
          color="primary"
          onClick={() => {
            navigate('/ora/novo');
          }}
          disabled={loading}
          tooltip="Criar nova oportunidade"
        >
          <FiPlus /> NOVA OPORTUNIDADE
        </Button>
      </header>
      {loading ? (
        <Loader />
      ) : oportunidades && oportunidades.length ? (
        <Scroll $height="calc(100vh - 215px)">
          <Table>
            <thead>
              <tr>
                <th>PROGRAMA</th>
                <th>TÍTULO</th>
                <th>PALAVRAS-CHAVE</th>
                <th>DATA DE EXIBIÇÃO</th>
                <th>CARD</th>
                <th>DATA DE CRIAÇÃO</th>
                <th>AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              {oportunidades.map(o => (
                <tr key={o.id}>
                  <td>{o.programa.mnemonico}</td>
                  <td>{o.titulo}</td>
                  <td>
                    {o.palavraschaves?.split(',').map(p => (
                      <button
                        type="button"
                        onClick={() => handlePalavraChaveSearch(p.trim())}
                      >
                        {p}
                      </button>
                    ))}
                  </td>
                  <td>
                    {format(
                      addHours(new Date(o.dataexibicao), 3),
                      'dd/MM/yyyy',
                    )}
                  </td>
                  <td>
                    <Flag type={o.statuscard} name={o.statuscard} />
                  </td>
                  <td>
                    {format(new Date(o.created_at), "dd/MM/yyyy' às 'HH:mm")}
                  </td>
                  <td>
                    <Button
                      fill
                      type="button"
                      color="font"
                      round
                      onClick={() => {
                        navigate(`/ora/${o.id}`);
                      }}
                      tooltip="Editar dados"
                    >
                      <FiEdit2 />
                    </Button>
                    <Button
                      fill
                      type="button"
                      color="font"
                      round
                      disabled={!o.card_url}
                      onClick={() => {
                        window.open(`${o.card_url}`, '_blank');
                      }}
                      tooltip="Visualizar card"
                    >
                      <FiImage />
                    </Button>
                    <Button
                      fill
                      color="font"
                      round
                      disabled={!o.titulo || !o.resumo}
                      onClick={() => {
                        handleRecriarCard(o);
                      }}
                      tooltip="Recriar card"
                    >
                      <FiRepeat />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      ) : (
        <NotFound img={NoSearch2} title="Oportunidades">
          Nenhuma OPORTUNIDADE encontrada
        </NotFound>
      )}
    </Content>
  );
}
